import React from "react";
import { FormattedMessage } from "react-intl";
import Icon, { ICON_SIZE, ICON_TYPE } from "components/Icon";
import { formatCurrency, formatDate } from "utils";
import Status from "components/Status";
import {
  APPLICANT_TYPES,
  AVAILABILITIES,
  CAPACITY_STATUS,
  USER_TYPES,
  MISSION_ROLE_STATES,
  APPLICANT_LIST_SORTING_OPTIONS,
} from "constants/index";
import { UserCapacity } from "components/User";
import { ProfileRateRange } from "components/Profile";
import Score from "components/Score";
import { RoleLink } from "components/Missions/Roles";
import { ActionButton } from "components/Buttons";
import { colors } from "style";
import { Col, Row } from "components/Containers";
import Tooltip from "components/Tooltip";
import { Tag, TAG_SIZE } from "components/Tags";
import Overlay from "components/Overlay";
import {
  ApplicantStatus,
  ApplicantChecklistDisplay,
  ApplicantLocation,
  ApplicantAppliedAt,
  ApplicantInvitedAt,
  ApplicantUpdatedAt,
  ApplicantIsInvited,
  ApplicantMissionActiveCount,
  ApplicantMissionLifetimeCount,
  ApplicantInvitationRate,
  ApplicantAppliedRank,
  ApplicantRateScore,
  ApplicantRate,
  ApplicantMatchScore2,
  ApplicantViewsCount,
  ChecklistPercentageHeader,
  ApplicantNotificationStatus,
  ApplicantLastTransitionActor,
} from "components/Missions/Applicants";
import { ColumnMember, ColumnDisciplines } from "components/Table/Columns";

const APPLICANT_LIST_COLUMNS = {
  distanceRank: {
    label: <FormattedMessage id="Missions.Applicants.ApplicantAppliedRank" />,
    width: 80,
    cell: (row) => <ApplicantAppliedRank scores={row.scores} />,
    restrict: {
      roles: [USER_TYPES.admin],
      states: [
        APPLICANT_TYPES.invited,
        APPLICANT_TYPES.recommended,
        APPLICANT_TYPES.pending,
        APPLICANT_TYPES.applied,
        APPLICANT_TYPES.shortlisted,
        APPLICANT_TYPES.interviewed,
        APPLICANT_TYPES.selected,
        APPLICANT_TYPES.unselected,
      ],
    },
  },
  status: {
    label: <FormattedMessage id="Missions.Applicants.ApplicantStatus" />,
    width: 100,
    cell: (row) => <ApplicantStatus data={row} />,
    restrict: {
      roles: [USER_TYPES.admin],
      states: [
        APPLICANT_TYPES.invited,
        APPLICANT_TYPES.recommended,
        APPLICANT_TYPES.pending,
        APPLICANT_TYPES.applied,
        APPLICANT_TYPES.shortlisted,
        APPLICANT_TYPES.interviewed,
        APPLICANT_TYPES.selected,
        APPLICANT_TYPES.unselected,
      ],
    },
  },
  member: ({ modalConfigFunc, ...props }) => ({
    label: <FormattedMessage id="Missions.Applicants.ApplicantMember" />,
    width: "50%",
    cell: (row) => (
      <ColumnMember
        showProfileFlag={props?.isAdmin}
        showHighlights={props?.isAdmin}
        showProfileAssigned={props?.isAdmin}
        data={row}
        {...props}
        {...(modalConfigFunc?.(row) || {})}
      />
    ),
    restrict: {
      roles: [USER_TYPES.showcase, USER_TYPES.client, USER_TYPES.admin],
      states: [
        APPLICANT_TYPES.invited,
        APPLICANT_TYPES.recommended,
        APPLICANT_TYPES.pending,
        APPLICANT_TYPES.applied,
        APPLICANT_TYPES.shortlisted,
        APPLICANT_TYPES.interviewed,
        APPLICANT_TYPES.selected,
        APPLICANT_TYPES.unselected,
      ],
    },
  }),
  memberWithDisciplines: ({ modalConfigFunc, isAdmin, ...props }) => ({
    label: <FormattedMessage id="Missions.Applicants.ApplicantMember" />,
    width: "50%",
    cell: (row) => (
      <ColumnMember
        data={row}
        showArchetype
        showDisciplines
        showProfileFlag={isAdmin}
        showHighlights={isAdmin}
        showProfileAssigned={isAdmin}
        {...props}
        {...(modalConfigFunc?.(row) || {})}
      />
    ),
    restrict: {
      roles: [USER_TYPES.admin],
      states: [
        APPLICANT_TYPES.invited,
        APPLICANT_TYPES.recommended,
        APPLICANT_TYPES.pending,
        APPLICANT_TYPES.applied,
        APPLICANT_TYPES.shortlisted,
        APPLICANT_TYPES.interviewed,
        APPLICANT_TYPES.selected,
        APPLICANT_TYPES.unselected,
      ],
    },
  }),
  disciplines: {
    label: <FormattedMessage id="MissionBoard.TableColumnDisciplines" />,
    cell: (row) => <ColumnDisciplines data={row?.profileData} />,
    width: "50%",
  },
  location: {
    label: <FormattedMessage id="Missions.Applicants.ApplicantLocation" />,
    width: 130,
    cell: ({ profileData }) => <ApplicantLocation {...profileData} />,
    restrict: {
      roles: [USER_TYPES.admin],
      states: [
        APPLICANT_TYPES.invited,
        APPLICANT_TYPES.recommended,
        APPLICANT_TYPES.pending,
        APPLICANT_TYPES.applied,
        APPLICANT_TYPES.shortlisted,
        APPLICANT_TYPES.interviewed,
        APPLICANT_TYPES.selected,
        APPLICANT_TYPES.unselected,
      ],
    },
  },
  checklistPercentage: ({ selectedState }) => {
    const selectedSortingOption = APPLICANT_LIST_SORTING_OPTIONS.find(({ value }) => value === selectedState?.sortKey);
    const tooltipId = selectedSortingOption?.columnTooltips?.checklistPercentage;

    return {
      label: <ChecklistPercentageHeader tooltipId={tooltipId} />,
      width: 83,
      cell: (row) => (
        <Overlay
          asCardOverlay
          overlay={<ApplicantChecklistDisplay missionRoleApplicant={row} />}
          trigger={
            <Score score={calculateApplicationCompletionPercentage(row.applicationChecklist)}>
              {Math.round(calculateApplicationCompletionPercentage(row.applicationChecklist))}%
            </Score>
          }
          placement="top"
          width="30rem"
        />
      ),
      restrict: {
        roles: [USER_TYPES.admin, USER_TYPES.client],
        states: [
          APPLICANT_TYPES.pending,
          APPLICANT_TYPES.applied,
          APPLICANT_TYPES.shortlisted,
          APPLICANT_TYPES.interviewed,
          APPLICANT_TYPES.selected,
          APPLICANT_TYPES.unselected,
        ],
      },
    };
  },
  missionStats: {
    label: <FormattedMessage id="Missions.Applicants.ApplicantStats" />,
    width: 200,
    cell: (row) => (
      <Col gap="0.3rem">
        <ApplicantAppliedAt data={row?.missionData} />
        <ApplicantInvitedAt data={row?.missionData} />
        <ApplicantUpdatedAt data={row} />
        <ApplicantIsInvited data={row?.missionData} />
        <ApplicantMissionActiveCount data={row?.missionData} />
        <ApplicantMissionLifetimeCount data={row?.missionData} />
        <ApplicantInvitationRate data={row?.missionData} />
        <ApplicantViewsCount viewsCount={row?.viewsCount} />
      </Col>
    ),
    restrict: {
      roles: [USER_TYPES.admin],
      states: [
        APPLICANT_TYPES.invited,
        APPLICANT_TYPES.recommended,
        APPLICANT_TYPES.pending,
        APPLICANT_TYPES.applied,
        APPLICANT_TYPES.shortlisted,
        APPLICANT_TYPES.interviewed,
        APPLICANT_TYPES.selected,
        APPLICANT_TYPES.unselected,
      ],
    },
  },
  mission: () => ({
    label: <FormattedMessage id="Missions.Applicants.ApplicantRoleMissionOrg" />,
    width: "50%",
    cell: (row) => <RoleLink mission={row?.missionRole?.mission} missionRole={row?.missionRole} withOverlay />,
    restrict: {
      roles: [USER_TYPES.admin, USER_TYPES.showcase, USER_TYPES.client, USER_TYPES.freelancer],
      states: [
        APPLICANT_TYPES.invited,
        APPLICANT_TYPES.recommended,
        APPLICANT_TYPES.pending,
        APPLICANT_TYPES.applied,
        APPLICANT_TYPES.shortlisted,
        APPLICANT_TYPES.interviewed,
        APPLICANT_TYPES.selected,
        APPLICANT_TYPES.unselected,
      ],
    },
  }),
  hourlyRateRange: {
    label: <FormattedMessage id="Missions.MissionCandidateList.HourlyRate" />,
    width: 200,
    cell: ({ sellingRateRange, negotiatedSellingRate }) => {
      if (negotiatedSellingRate?.amount) {
        return (
          <ProfileRateRange
            low={negotiatedSellingRate?.amount}
            high={negotiatedSellingRate?.amount}
            currency={negotiatedSellingRate?.currency}
          />
        );
      }

      return (
        <ProfileRateRange
          includesCommission={false}
          low={sellingRateRange?.hourly?.low}
          high={sellingRateRange?.hourly?.high}
          currency={sellingRateRange?.currency}
          assignedSellingRate={false}
        />
      );
    },
    restrict: {
      roles: [USER_TYPES.showcase, USER_TYPES.client],
      states: [
        APPLICANT_TYPES.recommended,
        APPLICANT_TYPES.applied,
        APPLICANT_TYPES.shortlisted,
        APPLICANT_TYPES.interviewed,
        APPLICANT_TYPES.selected,
        APPLICANT_TYPES.unselected,
      ],
    },
  },
  state: ({ isMember }) => ({
    label: <FormattedMessage id="Missions.Applicants.ApplicantState" />,
    width: 120,
    cell: (row) => {
      const isRoleClosed = row?.missionRole?.state === MISSION_ROLE_STATES.closed;
      const isFinalState = [APPLICANT_TYPES.selected, APPLICANT_TYPES.unselected].includes(row?.state);

      return isRoleClosed && !isFinalState ? (
        <Tag color={TAG_COLORS[APPLICANT_TYPES.unselected]} size={TAG_SIZE.medium}>
          <FormattedMessage id={`Missions.MissionCandidateList.State.RoleClosed`} />
        </Tag>
      ) : (
        <Tag color={getTagColorForState(row.state)} size={TAG_SIZE.medium}>
          <FormattedMessage
            id={`Missions.MissionCandidateList.State.${row.state}${
              row.state === APPLICANT_TYPES.pending && isMember ? ".Member" : ""
            }`}
          />
        </Tag>
      );
    },
  }),
  profileAvailability: {
    label: <FormattedMessage id="Missions.Applicants.ApplicantAvailability" />,
    width: "15%",
    cell: (row) => (
      <Status enabled={row?.profile?.capacityStatus === CAPACITY_STATUS.available}>
        <UserCapacity data={row?.profile} />
      </Status>
    ),
    restrict: {
      roles: [USER_TYPES.showcase, USER_TYPES.client],
      states: [APPLICANT_TYPES.recommended],
    },
  },
  applicationRate: {
    label: <FormattedMessage id="Missions.Applicants.ApplicantApplicationRate" />,
    width: 170,
    cell: (row) => (
      <Col>
        {row?.rate?.amount ? (
          <Row gap="0.25rem" noWrap>
            <Icon type={ICON_TYPE.checkDotted} size={ICON_SIZE.medium} color="inherit" />
            <Tooltip tooltipId={"Missions.Applicants.ApplicantList.ApplicantRate"}>
              <FormattedMessage
                id={row?.rate?.amount ? "Profile.ProfileRateAndAvailability.DataRate" : "Global.NotProvided"}
                values={{
                  amount: formatCurrency(row?.rate?.amount, row?.rate?.currency),
                  type: <FormattedMessage id={`Profile.ProfileRateAndAvailability.Frequency${row?.rate?.frequency}`} />,
                }}
              />
            </Tooltip>
          </Row>
        ) : (
          <Row gap="0.25rem" noWrap>
            <Icon type={ICON_TYPE.coin} size={ICON_SIZE.medium} color="inherit" />
            <Tooltip tooltipId={"Missions.Applicants.ApplicantList.ProfileRate"}>
              <FormattedMessage
                id={row?.profile?.rate?.amount ? "Profile.ProfileRateAndAvailability.DataRate" : "Global.NotProvided"}
                values={{
                  amount: formatCurrency(row?.profile?.rate?.amount, row?.profile?.rate?.currency),
                  type: (
                    <FormattedMessage
                      id={`Profile.ProfileRateAndAvailability.Frequency${row?.profile?.rate?.frequency}`}
                    />
                  ),
                }}
              />
            </Tooltip>
          </Row>
        )}
      </Col>
    ),
    restrict: {
      roles: [USER_TYPES.admin],
      states: [
        APPLICANT_TYPES.pending,
        APPLICANT_TYPES.applied,
        APPLICANT_TYPES.shortlisted,
        APPLICANT_TYPES.interviewed,
        APPLICANT_TYPES.selected,
        APPLICANT_TYPES.unselected,
      ],
    },
  },
  noticePeriod: {
    label: <FormattedMessage id="Missions.Applicants.ApplicantNoticePeriod" />,
    width: 110,
    cell: (row) =>
      !row.noticePeriod ? (
        <FormattedMessage id="Global.NotProvided" />
      ) : (
        AVAILABILITIES.find((e) => e.value === row.noticePeriod).label
      ),
    restrict: {
      roles: [USER_TYPES.showcase, USER_TYPES.client],
      states: [
        APPLICANT_TYPES.pending,
        APPLICANT_TYPES.applied,
        APPLICANT_TYPES.shortlisted,
        APPLICANT_TYPES.interviewed,
        APPLICANT_TYPES.selected,
        APPLICANT_TYPES.unselected,
      ],
    },
  },
  applied: {
    label: <FormattedMessage id="Missions.Applicants.ApplicantSubmittedAt" />,
    width: 110,
    cell: (row) => (row?.appliedAt ? formatDate(row?.appliedAt) : <FormattedMessage id="Global.EmDash" />),
    restrict: {
      roles: [USER_TYPES.admin, USER_TYPES.showcase, USER_TYPES.client, USER_TYPES.freelancer],
      states: [
        APPLICANT_TYPES.applied,
        APPLICANT_TYPES.shortlisted,
        APPLICANT_TYPES.interviewed,
        APPLICANT_TYPES.selected,
        APPLICANT_TYPES.unselected,
      ],
    },
  },
  created: {
    label: <FormattedMessage id="Missions.Applicants.ApplicantStartedAt" />,
    width: "10%",
    cell: (row) => formatDate(row?.createdAt),
  },
  matchScore: {
    label: <FormattedMessage id="Missions.MissionCandidateList.MatchScore" />,
    width: 80,
    cell: (row) => <ApplicantMatchScore2 scores={row.scores} matchScore={row.matchScore} scoreData={row.scoreData} />,
    restrict: {
      roles: [USER_TYPES.admin, USER_TYPES.showcase, USER_TYPES.client],
      states: [
        APPLICANT_TYPES.invited,
        APPLICANT_TYPES.recommended,
        APPLICANT_TYPES.pending,
        APPLICANT_TYPES.applied,
        APPLICANT_TYPES.shortlisted,
        APPLICANT_TYPES.interviewed,
        APPLICANT_TYPES.selected,
        APPLICANT_TYPES.unselected,
      ],
    },
  },
  rateScore: {
    label: <FormattedMessage id="Missions.MissionCandidateList.RateScore" />,
    width: 80,
    cell: (row) => <ApplicantRateScore hourlyRateData={row?.hourlyRateData} rateScore={row.rateScore} />,
    restrict: {
      roles: [USER_TYPES.admin],
      states: [
        APPLICANT_TYPES.invited,
        APPLICANT_TYPES.recommended,
        APPLICANT_TYPES.pending,
        APPLICANT_TYPES.applied,
        APPLICANT_TYPES.shortlisted,
        APPLICANT_TYPES.interviewed,
        APPLICANT_TYPES.selected,
        APPLICANT_TYPES.unselected,
      ],
    },
  },
  rate: ({ selectedState }) => ({
    width: 225,
    label: <FormattedMessage id="Missions.MissionCandidateList.Score" />,
    cell: (row) => {
      const isInvitedTab = selectedState?.applicantState === APPLICANT_TYPES.invited;
      const isRecommendedTab = selectedState?.applicantState === APPLICANT_TYPES.recommended;

      return (
        <ApplicantRate
          data={row}
          showBid={!isInvitedTab && !isRecommendedTab}
          showEstimate={isInvitedTab || isRecommendedTab}
        />
      );
    },
    restrict: {
      roles: [USER_TYPES.admin],
      states: [
        APPLICANT_TYPES.invited,
        APPLICANT_TYPES.recommended,
        APPLICANT_TYPES.pending,
        APPLICANT_TYPES.applied,
        APPLICANT_TYPES.shortlisted,
        APPLICANT_TYPES.interviewed,
        APPLICANT_TYPES.selected,
        APPLICANT_TYPES.unselected,
      ],
    },
  }),
  activity: {
    width: 150,
    label: <FormattedMessage id="Missions.Applicants.ApplicantActivity" />,
    cell: (row) => (
      <Col gap="0.5rem">
        <ApplicantLastTransitionActor actor={row?.lastTransitionActor} />
        <ApplicantNotificationStatus row={row} />
      </Col>
    ),
    restrict: {
      roles: [USER_TYPES.admin],
      states: [
        APPLICANT_TYPES.applied,
        APPLICANT_TYPES.shortlisted,
        APPLICANT_TYPES.interviewed,
        APPLICANT_TYPES.selected,
        APPLICANT_TYPES.unselected,
      ],
    },
  },
  viewButton: ({ setApplication }) => ({
    width: 60,
    cell: (row) => (
      <a
        href="#"
        onClick={(event) => {
          event.preventDefault();
          setApplication(row);
        }}
      >
        <ActionButton>
          <Icon type={ICON_TYPE.eye} size={ICON_SIZE.large} color={colors.purpleRainBase} />
        </ActionButton>
      </a>
    ),
    restrict: {
      roles: [USER_TYPES.admin, USER_TYPES.client, USER_TYPES.showcase],
      states: [
        APPLICANT_TYPES.pending,
        APPLICANT_TYPES.applied,
        APPLICANT_TYPES.shortlisted,
        APPLICANT_TYPES.interviewed,
        APPLICANT_TYPES.selected,
        APPLICANT_TYPES.unselected,
      ],
    },
  }),
  navigateToApplicationButton: ({ navigateToApplication }) => ({
    width: "5%",
    cell: (row) => (
      <ActionButton onClick={() => navigateToApplication(row)}>
        <Icon type={ICON_TYPE.eye} size={ICON_SIZE.large} color={colors.purpleRainBase} />
      </ActionButton>
    ),
    restrict: {
      roles: [USER_TYPES.freelancer],
      states: [
        APPLICANT_TYPES.pending,
        APPLICANT_TYPES.applied,
        APPLICANT_TYPES.shortlisted,
        APPLICANT_TYPES.interviewed,
        APPLICANT_TYPES.selected,
        APPLICANT_TYPES.unselected,
      ],
    },
  }),
};

const calculateApplicationCompletionPercentage = (data) => {
  if (!data || data.length === 0) return 0;

  const fulfilledCount = data.filter((item) => item.fulfilled).length;
  const totalCount = data.length;

  return (fulfilledCount / totalCount) * 100;
};

const TAG_COLORS = {
  [APPLICANT_TYPES.applied]: colors.grayAnatomyBase,
  [APPLICANT_TYPES.shortlisted]: colors.grayAnatomyBase,
  [APPLICANT_TYPES.interviewed]: colors.grayAnatomyBase,
  [APPLICANT_TYPES.selected]: colors.green,
  [APPLICANT_TYPES.unselected]: colors.red,
  [APPLICANT_TYPES.withdrawn]: colors.error,
};

const getTagColorForState = (state) => TAG_COLORS[state] || colors.grayAnatomyBase;

export { calculateApplicationCompletionPercentage };

export default APPLICANT_LIST_COLUMNS;
